import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { isMobileDevice } from '../utils/desktopApp';
import ModalBackdrop from './ModalBackdrop';
import { Search, X, ChevronDown } from 'lucide-react';

const SingleSelectDropdown = ({
    options,
    selectedOption,
    onSelectionChange,
    title,
    disabled = false,
    placement = 'bottom', // 'bottom' | 'top'
    heightClass = 'h-[40px]',
    Icon = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const menuRef = useRef(null);
    const searchInputRef = useRef(null);
    const [menuPosition, setMenuPosition] = useState(null);

    const [focusedIndex, setFocusedIndex] = useState(-1);
    const focusedOptionRef = useRef(null);

    const isMobile = useMemo(() => isMobileDevice(), []);

    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();

            const topPos = placement === 'top'
                ? rect.top + window.scrollY
                : rect.bottom + window.scrollY;

            setMenuPosition({
                top: topPos,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    }, [isOpen, placement]);

    useEffect(() => {
        if (isOpen && searchInputRef.current && !isMobile) {
            searchInputRef.current.focus();
        }
    }, [isOpen, menuPosition, isMobile]);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm]);

    useEffect(() => {
        if (isOpen && focusedOptionRef.current) {
            focusedOptionRef.current.scrollIntoView({
                block: 'nearest',
            });
        }
    }, [focusedIndex, isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            const currentlySelectedIndex = filteredOptions.findIndex(o => o.value === selectedOption);
            setFocusedIndex(currentlySelectedIndex > -1 ? currentlySelectedIndex : 0);
        } else {
            document.body.style.overflow = '';
            setSearchTerm('');
            setMenuPosition(null);
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, filteredOptions, selectedOption]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile) return;
            if (
                containerRef.current && !containerRef.current.contains(event.target) &&
                menuRef.current && !menuRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isMobile]);

    const displayLabel = useMemo(() => {
        const selected = options.find(o => o.value === selectedOption);
        return selected ? selected.label : title;
    }, [options, selectedOption, title]);

    const selectedOptionConfig = useMemo(
        () => options.find(o => o.value === selectedOption),
        [options, selectedOption]
    );
    const SelectedIcon = selectedOptionConfig?.Icon || selectedOptionConfig?.icon || Icon;

    const handleOptionClick = (optionValue) => {
        onSelectionChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
        containerRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (disabled) return;

        if (e.key === 'Escape') {
            if (isOpen) {
                e.stopPropagation();
                setIsOpen(false);
                containerRef.current?.focus();
            }
            return;
        }

        if (!isOpen) {
            if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                e.preventDefault();
                setIsOpen(true);
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                setSearchTerm(e.key);
                setIsOpen(true);
            }
            return;
        }

        if (filteredOptions.length > 0) {
            if (['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
            }

            switch (e.key) {
                case 'ArrowDown':
                    setFocusedIndex(prev => (prev + 1) % filteredOptions.length);
                    break;
                case 'ArrowUp':
                    setFocusedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
                    break;
                case 'Enter':
                case ' ':
                    if (focusedIndex >= 0) {
                        handleOptionClick(filteredOptions[focusedIndex].value);
                    }
                    break;
                case 'Tab':
                    setIsOpen(false);
                    containerRef.current?.focus();
                    break;
                default:
                    break;
            }
        } else if (e.key === 'Tab') {
            setIsOpen(false);
            containerRef.current?.focus();
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const positionClasses = placement === 'top'
        ? 'transform -translate-y-full -mt-2'
        : 'mt-2';

    const MobileDropdownModal = (
        <ModalBackdrop
            onClose={() => setIsOpen(false)}
            mobileBottomSheet={true}
        >
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-3xl sm:rounded-2xl overflow-hidden flex flex-col h-[80vh]">
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/30">
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-900 dark:text-white">{title || 'Selecionar'}</h3>
                    <button onClick={() => setIsOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Buscar portal..."
                            className="w-full pl-11 pr-4 py-3 text-sm bg-gray-100 dark:bg-gray-900 border-none rounded-xl focus:ring-2 focus:ring-teal-500/30 outline-none text-gray-900 dark:text-white font-medium"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                <div className="flex-grow overflow-y-auto px-4 pb-12">
                    {filteredOptions.map((o) => {
                        const isSelected = o.value === selectedOption;
                        const OptionIcon = o.Icon || o.icon || Icon;
                        return (
                            <button
                                key={o.value}
                                type="button"
                                className={`w-full text-left flex items-center px-4 py-4 rounded-2xl transition-all mb-2 ${isSelected
                                        ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 shadow-sm border border-teal-100 dark:border-teal-900/30'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                    }`}
                                onClick={() => handleOptionClick(o.value)}
                            >
                                <div className="flex items-center gap-3 flex-grow min-w-0">
                                    {OptionIcon && <OptionIcon size={18} className={`${isSelected ? 'text-teal-500' : 'text-gray-400'} shrink-0`} />}
                                    <span className={`text-[13px] truncate ${isSelected ? 'font-black uppercase tracking-widest' : 'font-bold'}`}>{o.label}</span>
                                </div>
                                {isSelected && (
                                    <div className="h-2 w-2 rounded-full bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.6)]"></div>
                                )}
                            </button>
                        );
                    })}
                    {filteredOptions.length === 0 && (
                        <div className="py-12 text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-bold">Nenhuma opção encontrada.</p>
                        </div>
                    )}
                </div>
            </div>
        </ModalBackdrop>
    );

    const DesktopDropdownMenu = (
        <div
            ref={menuRef}
            style={{
                position: 'absolute',
                top: menuPosition ? `${menuPosition.top}px` : '-9999px',
                left: menuPosition ? `${menuPosition.left}px` : '-9999px',
                width: menuPosition ? `${menuPosition.width}px` : 'auto',
                visibility: menuPosition ? 'visible' : 'hidden',
            }}
            className={`rounded-2xl shadow-2xl bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border border-gray-100 dark:border-gray-700/50 z-[500] transition-all duration-200 ease-out overflow-hidden ${positionClasses}`}
        >
            <div className="p-3 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/30">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Buscar..."
                        className="w-full pl-9 pr-3 py-2 text-[11px] bg-white dark:bg-gray-900 border-none rounded-lg focus:ring-2 focus:ring-teal-500/30 outline-none text-gray-900 dark:text-white font-bold"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                setIsOpen(false);
                                containerRef.current?.focus();
                            } else if (e.key === 'Tab') {
                                e.preventDefault();
                                setIsOpen(false);
                                containerRef.current?.focus();
                            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleKeyDown(e);
                            }
                        }}
                    />
                </div>
            </div>
            <div className="py-1.5 max-h-60 overflow-y-auto px-1.5">
                {filteredOptions.map((o, index) => {
                    const isFocused = index === focusedIndex;
                    const isSelected = o.value === selectedOption;
                    const OptionIcon = o.Icon || o.icon || Icon;
                    return (
                        <button
                            ref={isFocused ? focusedOptionRef : null}
                            key={o.value}
                            type="button"
                            className={`w-full text-left flex items-center px-3 py-2.5 text-[11px] rounded-xl transition-all mb-0.5 ${isSelected 
                                    ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 font-black uppercase tracking-widest' 
                                    : 'text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                } ${isFocused && !isSelected ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}
                            onClick={() => handleOptionClick(o.value)}
                        >
                            {OptionIcon && <OptionIcon size={14} className={`${isSelected ? 'text-teal-500' : 'text-gray-400'} shrink-0`} />}
                            <span className="ml-2.5 truncate">{o.label}</span>
                            {isSelected && (
                                <div className="ml-auto h-1.5 w-1.5 rounded-full bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.6)]"></div>
                            )}
                        </button>
                    )
                })}
                {filteredOptions.length === 0 && (
                    <p className="text-center text-[10px] text-gray-500 dark:text-gray-400 py-4 font-bold uppercase tracking-widest">Nenhuma opção encontrada.</p>
                )}
            </div>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className="relative"
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
        >
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full inline-flex items-center justify-between px-4 ${heightClass} text-[10px] font-black uppercase tracking-widest rounded-xl text-gray-800 dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none shadow-sm border border-gray-100 dark:border-gray-700/50`}
                disabled={disabled}
                tabIndex="-1"
            >
                <div className="flex items-center gap-2.5 truncate">
                    {SelectedIcon && <SelectedIcon size={14} className="text-gray-400 shrink-0" />}
                    <span className="truncate">{displayLabel}</span>
                </div>
                <ChevronDown size={14} className={`text-gray-400 ml-2 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && !disabled && (isMobile ? MobileDropdownModal : (menuPosition && ReactDOM.createPortal(DesktopDropdownMenu, document.body)))}
        </div>
    );

};

export default SingleSelectDropdown;
